import { useEffect, useState } from "react";
import { clearDashboardOrder } from "../Contexts/dashboardOrder";

// Experimental feature flags, persisted in this browser's localStorage. Toggled
// from the Widget Admin → Experimental Flags panel (internal users only); read
// anywhere with useExperimentalFlag so the UI reacts live to a toggle.

const STORAGE_KEY = "dashalt_experimental_flags";
const CHANGE_EVENT = "dashalt-experimental-flags-changed";

export interface ExperimentalFlags {
  showComponentReorderingMenu: boolean;
  cleanView: boolean;
  advancedSandboxAiDefaults: boolean;
  showLegacyControlPanel: boolean;
}

const DEFAULTS: ExperimentalFlags = {
  showComponentReorderingMenu: false,
  cleanView: false,
  advancedSandboxAiDefaults: false,
  showLegacyControlPanel: false,
};

export const EXPERIMENTAL_FLAG_META: {
  key: keyof ExperimentalFlags;
  label: string;
  description: string;
}[] = [
  {
    key: "showComponentReorderingMenu",
    label: "Show Component Reordering Menu",
    description:
      "Adds a toolbar button to reorder your dashboards and choose which one loads first. The order is saved in this browser.",
  },
  {
    key: "cleanView",
    label: "Clean View",
    description:
      "A flat, minimal look: the dashboard background becomes the same white as the widgets, and widgets drop their grey border for a seamless, print-like layout.",
  },
  {
    key: "advancedSandboxAiDefaults",
    label: "Advanced Sandbox AI Defaults",
    description:
      "Adds a Development tab to the Sandbox AI panel with a library of ready-made build-from-scratch prompts of increasing complexity, so you can exercise the assistant across widget types without writing prompts yourself.",
  },
  {
    key: "showLegacyControlPanel",
    label: "Show Legacy Control Panel",
    description:
      "Restores the deprecated Control Panel toolbar button that bulk-applies one date range and subject selection to every widget. The Slicer widget replaces it.",
  },
];

export function getExperimentalFlags(): ExperimentalFlags {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
  } catch {
    return { ...DEFAULTS };
  }
}

export function setExperimentalFlags(flags: ExperimentalFlags): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    // localStorage unavailable — flags simply don't persist.
  }
}

/**
 * Turn every experimental feature off and clear all experimental data it stored in
 * this browser (the flags themselves + the custom dashboard order). Add any future
 * experimental storage cleanup here so one reset wipes it all.
 */
export function resetExperimentalFeatures(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  clearDashboardOrder();
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Reactively read one flag; updates when it's toggled (this tab or another). */
export function useExperimentalFlag(key: keyof ExperimentalFlags): boolean {
  const [value, setValue] = useState(() => getExperimentalFlags()[key]);
  useEffect(() => {
    const sync = () => setValue(getExperimentalFlags()[key]);
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync); // cross-tab
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [key]);
  return value;
}
